import angular from "angular";
import {
  Ajax, Calculation, Calculations, Configs, InputEnumOption,
  Invoice, Invoices, InvoicesLogic, InvoicePreview, InvoiceStatus, InvoiceType,
  PaymentChannel, PayrollDetails, PayrollStatus, InvoicePaymentType, SalaryDateKind, Translations, CalculationListItem, PayrollListItem, Payrolls4
} from "@salaxy/core";

import { InvoicesService, SessionService, UiHelpers } from "../../services";
import { ApiCrudObjectController } from "../bases";
import { CalculationCrudController, PayrollCrudController } from "../calc";
import { } from "../../components";

/**
 * Controller for the new Payment button and other functionality:
 * Creating (recreating) Invoices based on Calculations and Payrolls.
 */
export class InvoicesCreateController implements angular.IController {

  /**
   * For NG-dependency injection
   * @ignore
   */
  public static $inject = ["Invoices", "InvoicesService", "Calculations", "SessionService", "UiHelpers", "$location", "AjaxNg1"];

  /**
   * Defines the binding mode of the component.
   * Currently, only "model" is supported (Calculation or PayrollDetails),
   * but we might support e.g. id in the future.
   */
  public bindingMode: "model" | "null";

  /**
   * Collection of invoices if the channel and model have been set.
   * Null if the collection is being fetched.
   */
  public invoices: InvoicePreview[] = [];

  /** User canceled the process. */
  public userCanceled = false;

  private _current: Calculation | PayrollDetails;

  private _model: any;

  private _primaryCalcNetInvoicePreview: InvoicePreview = null;

  private _primaryCalcTaxInvoicePreview: InvoicePreview = null;

  constructor(
    private invoicesApi: Invoices,
    private invoicesService: InvoicesService,
    private calculationsApi: Calculations,
    private sessionService: SessionService,
    protected uiHelpers: UiHelpers,
    private $location: angular.ILocationService,
    private ajax: Ajax,
  ) {
  }

  /**
   * Implement IController by providing onInit method.
   * We currently do nothing here, but if you override this function,
   * you should call this method in base class for future compatibility.
   */
  public $onInit() {
    this.bindingMode = this.bindingMode || "null";
  }

  /**
   * Set the Current selected item (the model).
   * Currently, only Calculation or PayrollDetails is supported,
   * but we will probably support controller and url in the future.
   */
  public set model(value: Calculation | PayrollDetails | "url" | string | ApiCrudObjectController<Calculation, CalculationListItem> | ApiCrudObjectController<PayrollDetails, PayrollListItem>) {
    this._model = value;
    if (InvoicesLogic.isCalculation(value) || InvoicesLogic.isPayroll(value)) {
      this._current = value;
      this.bindingMode = "model";
    } else {
      throw new Error("Not implemented: Only Calculation or PayrollDetails is currently supported.");
    }
    this.reload();
  }
  /** Gets the Current selected item (the model). */
  public get model(): Calculation | PayrollDetails | "url" | string | ApiCrudObjectController<Calculation, CalculationListItem> | ApiCrudObjectController<PayrollDetails, PayrollListItem> {
    return this._model;
  }

  /** Changes the channel */
  public set channel(value: PaymentChannel) {
    if (InvoicesLogic.isCalculation(this.current)) {
      this.current.info.paymentChannel = value;
    } else {
      this.current.input.paymentChannel = value;
    }
    this.reload();
  }
  /** The current payment channel for user interface purposes. */
  public get channel(): PaymentChannel {
    if (!this.current) {
      return null;
    }
    const channel = InvoicesLogic.isCalculation(this.current)
      ? this.current.info.paymentChannel
      : this.current.input.paymentChannel;
    return channel || this.invoicesService.defaultChannel;
  }

  /** Gets the enabled channels. */
  public get allChannels() {
    return this.invoicesService.channelEnumOptions;
  }

  /** Gets the text for the Primary submit button. Null for hiding the button. */
  public getOkBtn(): string {
    if (this.userCanceled) {
      return null;
    }
    if (!this.isValid) {
      return null;
    }
    switch (this.paymentStatus) {

      case "paid":
      case "exceptions":
      case "canceled":
      case "unknown":
        return null;
      case "preview":
        if (InvoicesLogic.isPalkkausManualLike(this.channel) && this.salaryDateKind == SalaryDateKind.InPast) {
          return Translations.get("SALAXY.NG1.InvoicesCreateComponent.PaymentChannel.createAndMarkPaid");
        }
        if (InvoicesLogic.isPalkkausManualLike(this.channel) || this.channel === PaymentChannel.PalkkausWS || this.channel === PaymentChannel.FinagoSolo || this.channel === PaymentChannel.AccountorGo) {
          // for Accountor Go service users
          if(this.sessionService.isInRole("accountorService")){
            return null;
          }
          return Translations.get("SALAXY.NG1.InvoicesCreateComponent.PaymentChannel.createPayments");
        }
        if (InvoicesLogic.isPalkkausInvoicesLike(this.channel)) {
          return Translations.get("SALAXY.NG1.InvoicesCreateComponent.PaymentChannel.createInvoices");
        }
        if (this.channel === PaymentChannel.Hausvise) {
          return Translations.get("SALAXY.NG1.InvoicesCreateComponent.PaymentChannel.createPaymentMaterial");
        }
        return Translations.get("SALAXY.NG1.InvoicesCreateComponent.PaymentChannel.sendForPayment");
      case "inProgress":
        if (this.channel === PaymentChannel.Procountor || this.channel === PaymentChannel.VismaNetvisor || this.channel === PaymentChannel.Test) {
          return Translations.get("SALAXY.NG1.InvoicesCreateComponent.PaymentChannel.checkPayment");
        }
        return null;
      default:
        if (!this.channel || this.channel === PaymentChannel.Undefined) {
          return null;
        }
        if (InvoicesLogic.isPalkkausInvoicesLike(this.channel)) {
          return Translations.get("SALAXY.NG1.InvoicesCreateComponent.PaymentChannel.createInvoices");
        }
        if (this.channel === PaymentChannel.Hausvise) {
          return Translations.get("SALAXY.NG1.InvoicesCreateComponent.PaymentChannel.createPaymentMaterial");
        }
        return Translations.get("SALAXY.NG1.InvoicesCreateComponent.PaymentChannel.sendForPayment");
    }
  }

  /**
   * Intro text for the dialog.
   */
  public get intro(): string {
    if (InvoicesLogic.isPalkkausManualLike(this.channel)) {
      return this.salaryDateKind == SalaryDateKind.InPast ?
        Translations.get(`SALAXY.NG1.InvoicesCreateComponent.PaymentChannel.palkkausManualAndFixed.intro`) :
        Translations.get(`SALAXY.NG1.InvoicesCreateComponent.PaymentChannel.palkkausManual.intro`);
    }
    return Translations.get(`SALAXY.NG1.InvoicesCreateComponent.PaymentChannel.${this.channel}.intro`);
  }
  /** Title for the dialog */
  public get title(): string {
    switch (this.channel) {
      case PaymentChannel.PalkkausManual:
      case PaymentChannel.TalenomManual:
        return this.salaryDateKind == SalaryDateKind.InPast ?
          Translations.get("SALAXY.NG1.InvoicesCreateComponent.PaymentChannel.palkkausManualAndFixed.heading") :
          Translations.get("SALAXY.NG1.InvoicesCreateComponent.PaymentChannel.palkkausManual.heading");
      case PaymentChannel.PalkkausWS:
        return Translations.get("SALAXY.NG1.InvoicesCreateComponent.PaymentChannel.palkkausWS.heading");
      case PaymentChannel.PalkkausInvoices:
        return Translations.get("SALAXY.NG1.InvoicesCreateComponent.PaymentChannel.palkkausInvoices.heading");
      case PaymentChannel.Kitsas:
        return Translations.get("SALAXY.NG1.InvoicesCreateComponent.PaymentChannel.kitsas.heading");
      case PaymentChannel.FinagoSolo:
        return Translations.get("SALAXY.NG1.InvoicesCreateComponent.PaymentChannel.finagoSolo.heading");
      case PaymentChannel.AccountorGo:
        return Translations.get("SALAXY.NG1.InvoicesCreateComponent.PaymentChannel.accountorGo.heading");
      case PaymentChannel.Askelma:
        return Translations.get("SALAXY.NG1.InvoicesCreateComponent.PaymentChannel.askelma.heading");
      case PaymentChannel.Briox:
        return Translations.get("SALAXY.NG1.InvoicesCreateComponent.PaymentChannel.briox.heading");
      case PaymentChannel.Lemonaid:
        return Translations.get("SALAXY.NG1.InvoicesCreateComponent.PaymentChannel.lemonaid.heading");
      case PaymentChannel.Laskupiste:
        return Translations.get("SALAXY.NG1.InvoicesCreateComponent.PaymentChannel.laskupiste.heading");
      case PaymentChannel.PasseliMerit:
        return Translations.get("SALAXY.NG1.InvoicesCreateComponent.PaymentChannel.passeliMerit.heading");
      case PaymentChannel.Odoo:
        return Translations.get("SALAXY.NG1.InvoicesCreateComponent.PaymentChannel.odoo.heading");
      case PaymentChannel.Hausvise:
        return Translations.get("SALAXY.NG1.InvoicesCreateComponent.PaymentChannel.hausvise.heading");
      case PaymentChannel.Ecom:
        return Translations.get("SALAXY.NG1.InvoicesCreateComponent.PaymentChannel.ecom.heading");
      case PaymentChannel.Kompassi:
        return Translations.get("SALAXY.NG1.InvoicesCreateComponent.PaymentChannel.kompassi.heading");
      case PaymentChannel.Domus:
        return Translations.get("SALAXY.NG1.InvoicesCreateComponent.PaymentChannel.domus.heading");
      case PaymentChannel.Holvi:
        return Translations.get("SALAXY.NG1.InvoicesCreateComponent.PaymentChannel.holvi.heading");
      case PaymentChannel.TalenomOnline:
        return Translations.get("SALAXY.NG1.InvoicesCreateComponent.PaymentChannel.talenomOnline.heading");
      case PaymentChannel.PalkkausPersonal:
      default:
        return Translations.get("SALAXY.NG1.PalkkausPersonalPaymentOverviewComponent.loadingPayment");
    }
  }

  /** Gets the Current selected item. */
  public get current(): Calculation | PayrollDetails {
    return this._current;
  }

  /**
   * Returns true if the invoices have been created => the payment process has been started.
   * It may have been completed (paid), canceled or rejected.
   */
  public get isStarted(): boolean {
    if (InvoicesLogic.isPayroll(this.current) && this.current.info.status !== PayrollStatus.Draft) {
      return true;
    } else if (InvoicesLogic.isCalculation(this.current) && this.current.isReadOnly) {
      return true;
    }
    return false;
  }

  /**
   * Gets the payment status of the invoices.
   *
   * - unknown: The invoices have not been loaded from the server yet.
   * - preview: The invoices have not yet been created
   * - inProgress: Any of the invoices is in progress (WaitingPalkkaus,Unread, Read, WaitingConfirmation, PaymentStarted)
   * OR some invoices have been paid, some not (default state).
   * - canceled: One of the invoices is canceled.
   * - exceptions: Any of the invoices have exceptions (Undefined, Error)
   * - paid: All the invoices have been paid
   *
   * This is ready only after the invoices have been loaded by Preview (may be already created invoices).
   */
  public get paymentStatus(): "unknown" | "preview" | "inProgress" | "exceptions" | "canceled" | "paid" {

    const invoices = (this.invoices ?? []).filter(x => x.invoice.entityType !== "payroll");

    if (invoices.length === 0) {
      return "unknown";
    }

    if (invoices.every((x) => !x.exists)) {
      return "preview";
    }

    const paidNet = invoices.find((x) => x.invoice.status == InvoiceStatus.Paid && x.invoice.entityType == "net");
    if (paidNet?.invoice.data.channel == PaymentChannel.PalkkausPersonal) {
      // HACK: As of writing PalkkausPersonal only sets Net payment as paid (not the tax).
      return "paid";
    }

    const inProgress = [InvoiceStatus.WaitingPalkkaus, InvoiceStatus.Unread, InvoiceStatus.Read, InvoiceStatus.WaitingConfirmation, InvoiceStatus.PaymentStarted];
    if (invoices.find((x) => inProgress.indexOf(x.invoice.status) >= 0)) {
      return "inProgress";
    }
    if (invoices.find((x) => x.invoice.status === InvoiceStatus.Canceled)) {
      return "canceled";
    }
    const exceptions = [InvoiceStatus.Undefined, InvoiceStatus.Error];
    if (invoices.find((x) => exceptions.indexOf(x.invoice.status) >= 0)) {
      return "exceptions";
    }
    const paid = [InvoiceStatus.Paid, InvoiceStatus.Forecast, InvoiceStatus.Preview];
    if (invoices.every((x) => paid.indexOf(x.invoice.status) >= 0)) {
      return "paid";
    }
    return "inProgress";
  }

  /** Gets the type of current. */
  public get currentType(): "Calculation" | "PayrollDetails" | null {
    if (!this.current) {
      return null;
    }
    if (InvoicesLogic.isPayroll(this.current)) {
      return "PayrollDetails";
    }
    if (InvoicesLogic.isCalculation(this.current)) {
      return "Calculation";
    }
    return null;
  }

  /** Returns true if the calculations are valid for payment. */
  public get isValid(): boolean {
    if (InvoicesLogic.isPayroll(this.current)) {
      // return this.current.validations.every((x) => x.isValid);
      return this.current.info &&
        this.current.info.isReadyForPayment;
    }
    if (InvoicesLogic.isCalculation(this.current)) {
      return this.current.result &&
        // this.current.result.validation?.isValid &&
        !(this.current.result.validation?.errors ?? []).find((e) => (e.type !== "warning")) &&
        this.current.worker &&
        this.current.worker.tax.isValid;
    }
    return false;
  }

  /** Gets and avatar image for a payment channel */
  public getChannelAvatar(option: InputEnumOption) {
    const channel = option.value || PaymentChannel.Undefined;
    return InvoicesLogic.getChannelAvatar(channel as PaymentChannel);
  }

  /** Reloads the preview invoices from the server. */
  public reload() {
    if (!this.channel || this.channel === PaymentChannel.Undefined || !this.isValid) {
      return; // Cannot start reload
    }
    if (this.bindingMode == null) {
      return;
    }
    if (this.bindingMode !== "model") {
      throw new Error("Not implemented: Only Calculation or PayrollDetails is currently supported.");
    }
    this.invoices = null;
    if (InvoicesLogic.isPayroll(this.current)) {

      if (this.current.calcs?.length > 0) {
        // old
        this.invoicesApi.previewInvoices(this.channel, { payrollIds: [this.current.id] }).then((result) => {
          this.invoices = result;
        });
      }
      else {
        // new
        const payrolls4Api = new Payrolls4(this.ajax);
        payrolls4Api.getCalcsOData(this.current.id, null).then((listItemResult) => {
          const calcs = listItemResult.items;
          const data: any = {};
          data.listItemType = "CalculationListItem";
          const columns = undefined;
          const paramsTemplate = undefined;
          const action = (items, data) => {
            const itemIds = items.map((x) => x.id);
            const remainingAfterThis =  data.dialogList.filter((x) => !x.processed).length;
            const excludePeriodic = remainingAfterThis == 0 ? false:  true;
            return payrolls4Api.previewInvoices(this.current.id, itemIds, excludePeriodic).then((result) => {
              const results = items.map(() => ( {message: "", result: []}));
              if (results.length > 0) {
                results[0].result = result;
              }
              return results;
            });
          };
          this.uiHelpers.showBatchProcessDialog(Translations.get("SALAXY.NG1.InvoicesCreateComponent.uiStatusLoading"), calcs, action, paramsTemplate, data, columns, 20, true, true, true).then((dialogResult) => {
            if (dialogResult.done) {
              this.invoices = [];
              dialogResult.results.forEach( (result) => {
                this.invoices.push(...result.result);
              });
            }
            else
            {
              this.userCanceled = true;
              this.invoices = [];
            }
          });
        });
      }
    }
    else if (InvoicesLogic.isCalculation(this.current)) {
      this.invoicesApi.previewInvoices(this.channel, { calcs: [this.current] }).then((result) => {
        this.invoices = result;
      });
    }
  }

  /** Gets the part of the UI that should be visible. */
  public get uiStatus(): "invalid" | "loading" | "created" | "supportedChannel" | "unsupported" | "userCanceled" | "undefined" {
    if (this.userCanceled) {
      return "userCanceled"; // User canceled the process
    }
    if (!this.isValid) {
      return "invalid"; // Show validation errors
    }
    if (this.paymentStatus === "unknown") {
      return "loading"; // this.invoices is still loading => Show spinner
    }
    if (this.paymentStatus === "exceptions"
      || this.paymentStatus === "canceled"
      || this.paymentStatus === "inProgress"
      || this.paymentStatus === "paid"
    ) {
      return "created"; // Invoices have already been created (no swithching channel).
    }
    if (!this.channel || this.channel === PaymentChannel.Undefined) {
      return "undefined"; // Choose channel
    }
    const supportedChannels = [
      PaymentChannel.AccountorGo,
      PaymentChannel.FinagoSolo,
      PaymentChannel.Kevytyrittaja,
      PaymentChannel.Askelma,
      PaymentChannel.Briox,
      PaymentChannel.Lemonaid,
      PaymentChannel.Laskupiste,
      PaymentChannel.PasseliMerit,
      PaymentChannel.Odoo,
      PaymentChannel.Hausvise,
      PaymentChannel.Ecom,
      PaymentChannel.Kompassi,
      PaymentChannel.Domus,
      PaymentChannel.Holvi,
      PaymentChannel.PalkkausManual,
      PaymentChannel.TalenomManual,
      PaymentChannel.PalkkausWS,
      PaymentChannel.PalkkausPersonal,
      PaymentChannel.PalkkausInvoices,
      PaymentChannel.Kitsas,
      PaymentChannel.Test,
      PaymentChannel.ZeroPayment,
      PaymentChannel.TalenomOnline,
      PaymentChannel.VismaNetvisor,
      PaymentChannel.Procountor
    ];
    if (supportedChannels.find((x) => x === this.channel)) {
      return "supportedChannel"; // Supported channel
    }
    return "unsupported";
  }

  /** Returns current salary date kind */
  public get salaryDateKind(): SalaryDateKind {
    let salaryDateKind: SalaryDateKind = undefined;
    if (InvoicesLogic.isPayroll(this.current)) {
      salaryDateKind = this.current.input.salaryDateKind;
    }
    else if (InvoicesLogic.isCalculation(this.current)) {
      salaryDateKind = this.current.workflow.salaryDateKind;
    }
    return salaryDateKind ?? SalaryDateKind.Default;
  }

  /**
   * Starts the invoices creation process from the current channel and model.
   * This method is used when controller is used first as previe and then creating invoices.
   */
  public createInvoices(closeFunc?: (action) => any) {
    if (!this.channel || this.channel === PaymentChannel.Undefined || !this.isValid) {
      throw new Error("Cannot start invoices creation");
    }

    if (!this.invoicesService.checkAccountVerificationForPayment()) {
      this.invoicesService.showMissingSignatureAlert();
      return;
    }

    if (!this.invoicesService.checkChangedSalaryDate(this.current)) {
      this.invoicesService.showChangedSalaryDateAlert();
      return;
    }

    if(InvoicesLogic.isEcfa(this.channel)) {
      if(!this.invoicesService.checkKycForPayment()) {
        this.invoicesService.showMissingKycAlert();
        return;
      }
    }

    const inProgress = [InvoiceStatus.WaitingPalkkaus, InvoiceStatus.Read, InvoiceStatus.WaitingConfirmation, InvoiceStatus.PaymentStarted];

    // TODO: We should allow redirection for payroll payments to TalenomOnline and Procountor, like with InvoicesLogic.isCalculation.
    if (InvoicesLogic.isPayroll(this.current)) {
      if (this.channel === PaymentChannel.Test || this.channel === PaymentChannel.VismaNetvisor || this.channel === PaymentChannel.Procountor) {
        if (this.paymentStatus === "inProgress") {
          const loader = this.uiHelpers.showLoading("SALAXY.NG1.InvoicesCreateComponent.PaymentChannel.paymentDialogOpened");
          const idsToPay = this.invoices.filter((x) => inProgress.indexOf(x.invoice.status) >= 0).map((x) => x.invoice.id);
          let url = "";
          switch (this.channel) {
            case PaymentChannel.Test:
              url =
                Configs.current.isTestData ?
                  "https://test-integrations.salaxy.com/test#/dialogs/payment" :
                  "https://integrations.salaxy.com/test#/dialogs/payment";
              break;
            case PaymentChannel.Procountor:
              url =
                Configs.current.isTestData ?
                  "http://localhost:93/procountor/payment.html" :
                  "https://integrations.salaxy.com/procountor/payment.html";
              break;
            case PaymentChannel.VismaNetvisor:
              url =
                Configs.current.isTestData ?
                  "http://localhost:93/vismanetvisor/payment.html" :
                  "https://integrations.salaxy.com/vismanetvisor/payment.html";
              break;
          }
          url += "?invoiceIds=" + idsToPay + "&token=" + this.ajax.getCurrentToken();
          this.uiHelpers.showExternalDialog("payrollPayment", url, {}).then(() => {
            loader.dismiss();
            // HACK: Need a way to configure the URL and potentially raise an event for reload.
            this.$location.path("/payroll/details/" + this.current.id + "&refresh=" + new Date().getTime());
            if (closeFunc) {
              closeFunc("ok");
            }
          });
        } else {
          const loader = this.uiHelpers.showLoading("SALAXY.NG1.InvoicesCreateComponent.PaymentChannel.creatingInvoices");
          this.invoicesApi.createInvoicesForPayroll(this.channel, this.current.id).then((result) => {
            loader.setHeading("SALAXY.NG1.InvoicesCreateComponent.PaymentChannel.paymentDialogOpened");
            const idsToPay = result.filter((x) => x.header.status === InvoiceStatus.Unread).map((x) => x.id);
            let url = "";
            switch (this.channel) {
              case PaymentChannel.Test:
                url =
                  Configs.current.isTestData ?
                    "https://test-integrations.salaxy.com/test#/dialogs/payment" :
                    "https://integrations.salaxy.com/test#/dialogs/payment";
                break;
              case PaymentChannel.Procountor:
                url =
                  Configs.current.isTestData ?
                    "http://localhost:93/procountor/payment.html" :
                    "https://integrations.salaxy.com/procountor/payment.html";
                break;
              case PaymentChannel.VismaNetvisor:
                url =
                  Configs.current.isTestData ?
                    "http://localhost:93/vismanetvisor/payment.html" :
                    "https://integrations.salaxy.com/vismanetvisor/payment.html";
                break;
            }
            url += "?invoiceIds=" + idsToPay + "&token=" + this.ajax.getCurrentToken();
            this.uiHelpers.showExternalDialog("payrollPayment", url, {}).then(() => {
              loader.dismiss();
              // HACK: Need a way to configure the URL and potentially raise an event for reload.
              this.$location.path("/payroll/details/" + this.current.id + "&refresh=" + new Date().getTime());
              if (closeFunc) {
                closeFunc("ok");
              }
            });
          });
        }
      } else {
        if (this.current.calcs?.length > 0) {
          // old
          const loader = this.uiHelpers.showLoading("SALAXY.NG1.InvoicesCreateComponent.PaymentChannel.creatingInvoices");
          this.invoicesApi.createInvoicesForPayroll(this.channel, this.current.id).then(() => {
            loader.dismiss();
            // HACK: Need a way to configure the URL and potentially raise an event for reload.
            this.$location.path("/payroll/details/" + this.current.id + "&refresh=" + new Date().getTime());
            this.showInvoiceSuccessAlert(this.channel);
            if (closeFunc) {
              closeFunc("ok");
            }
          });
        } else {
          // new
          const payrolls4Api = new Payrolls4(this.ajax);
          payrolls4Api.getCalcsOData(this.current.id, null).then((listItemResult) => {
            const calcs = listItemResult.items;
            const data: any = {};
            data.listItemType = "CalculationListItem";
            const columns = undefined;
            const paramsTemplate = undefined;
            const action = (items, data) => {
              const itemIds = items.map((x) => x.id);
              const remainingAfterThis =  data.dialogList.filter((x) => !x.processed).length;
              const excludePeriodic = remainingAfterThis == 0 ? false:  true;
              return payrolls4Api.createInvoices(this.current.id, itemIds, excludePeriodic).then((result) => {
                const results = items.map(() => ( {message: "", result: []}));
                if (results.length > 0) {
                  results[0].result = result;
                }
                return results;
              });
            };
            this.uiHelpers.showBatchProcessDialog(Translations.get("SALAXY.NG1.InvoicesCreateComponent.PaymentChannel.creatingInvoices"), calcs, action, paramsTemplate, data, columns, 20, true, true, true).then((dialogResult) => {
              if (dialogResult.done) {
                // HACK: Need a way to configure the URL and potentially raise an event for reload.
                this.$location.path("/payroll/details/" + this.current.id + "&refresh=" + new Date().getTime());
                this.showInvoiceSuccessAlert(this.channel);
                if (closeFunc) {
                  closeFunc("ok");
                }
              } else {
                this.userCanceled = true;
              }
            });
          });
        }
      }
    } else if (InvoicesLogic.isCalculation(this.current)) {

      const refreshCurrentCalculation = (invoices: Invoice[]): Promise<Calculation> => {
        if (invoices) {
          const calcId = (invoices.find((x) => x.header.type === InvoiceType.Net || x.header.type === InvoiceType.Gross))?.header.businessObjects[0];
          if (calcId) {
            return this.calculationsApi.getSingle(calcId).then((calculation) => {
              angular.copy(calculation, this.current);
              return this.current;
            });
          }
        }
        return Promise.resolve(this.current);
      };
      if (this.channel === PaymentChannel.Test || this.channel === PaymentChannel.VismaNetvisor || this.channel === PaymentChannel.Procountor) {
        if (this.paymentStatus === "inProgress") {
          const loader = this.uiHelpers.showLoading("SALAXY.NG1.InvoicesCreateComponent.PaymentChannel.paymentDialogOpened");
          const idsToPay = this.invoices.filter((x) => inProgress.indexOf(x.invoice.status) >= 0).map((x) => x.invoice.id);
          let url = "";
          switch (this.channel) {
            case PaymentChannel.Test:
              url =
                Configs.current.isTestData ?
                  "https://test-integrations.salaxy.com/test#/dialogs/payment" :
                  "https://integrations.salaxy.com/test#/dialogs/payment";
              break;
            case PaymentChannel.Procountor:
              url =
                Configs.current.isTestData ?
                  "http://localhost:93/procountor/payment.html" :
                  "https://integrations.salaxy.com/procountor/payment.html";
              break;
            case PaymentChannel.VismaNetvisor:
              url =
                Configs.current.isTestData ?
                  "http://localhost:93/vismanetvisor/payment.html" :
                  "https://integrations.salaxy.com/vismanetvisor/payment.html";
              break;
          }
          url += "?invoiceIds=" + idsToPay + "&token=" + this.ajax.getCurrentToken();
          this.uiHelpers.showExternalDialog("calcPayment", url, {}).then(() => {
            refreshCurrentCalculation(null).then(() => {
              loader.dismiss();
              // HACK: Need a way to configure the URL and potentially raise an event for reload.
              this.$location.path("/calc/details/" + this.current.id + "&refresh=" + new Date().getTime());
            });
            if (closeFunc) {
              closeFunc("ok");
            }
          });
        } else {
          const loader = this.uiHelpers.showLoading("SALAXY.NG1.InvoicesCreateComponent.PaymentChannel.creatingInvoices");
          this.invoicesApi.createInvoices(this.current, this.channel).then((result) => {
            loader.setHeading("SALAXY.NG1.InvoicesCreateComponent.PaymentChannel.paymentDialogOpened");
            let idsToPay = result.filter((x) => x.header.status === InvoiceStatus.Unread).map((x) => x.id);
            let url = "";
            switch (this.channel) {
              case PaymentChannel.Test:
                url =
                  Configs.current.isTestData ?
                    "https://test-integrations.salaxy.com/test#/dialogs/payment" :
                    "https://integrations.salaxy.com/test#/dialogs/payment";
                break;
              case PaymentChannel.Procountor:
                url =
                  Configs.current.isTestData ?
                    "http://localhost:93/procountor/payment.html" :
                    "https://integrations.salaxy.com/procountor/payment.html";
                break;
              case PaymentChannel.VismaNetvisor:
                idsToPay = result
                  .filter(v => v.header.paymentType === InvoicePaymentType.Salary && v.header.status === InvoiceStatus.Unread)
                  .map(v => v.id);
                url =
                  Configs.current.isTestData ?
                    "http://localhost:93/vismanetvisor/payment.html" :
                    "https://integrations.salaxy.com/vismanetvisor/payment.html";
                break;
            }
            url += "?invoiceIds=" + idsToPay + "&token=" + this.ajax.getCurrentToken();
            this.uiHelpers.showExternalDialog("calcPayment", url, {}).then(() => {
              refreshCurrentCalculation(result).then(() => {
                loader.dismiss();
                // HACK: Need a way to configure the URL and potentially raise an event for reload.
                this.$location.path("/calc/details/" + this.current.id + "&refresh=" + new Date().getTime());
              });
              if (closeFunc) {
                closeFunc("ok");
              }
            });
          });
        }
      } else {
        const loader = this.uiHelpers.showLoading("SALAXY.NG1.InvoicesCreateComponent.PaymentChannel.creatingInvoices");
        this.invoicesApi.createInvoices(this.current, this.channel).then((result) => {
          refreshCurrentCalculation(result).then(() => {
            loader.dismiss();
            // HACK: Need a way to configure the URL and potentially raise an event for reload.
            if ((this.current as Calculation)?.info?.payrollId) {
              this.$location.path("/payroll/details/" + (this.current as Calculation).info.payrollId + "&refresh=" + new Date().getTime());
            }
            else
            {
              this.$location.path("/calc/details/" + this.current.id + "&refresh=" + new Date().getTime());
            }
            this.showInvoiceSuccessAlert(this.channel);
          });
          if (closeFunc) {
            closeFunc("ok");
          }
        });
      }
    }
  }

  /**
   * Create invoices for calculation: Immediate execution without preview. Used in PRO and other advanced UI.
   * Shows a loader dialog, saves changes if necessary, creates invoices and reloads the controller.
   * @param calcController Controller that contains the calculation that should be paid. May have save changes.
   * @param channel Payment channel that is used for payment.
   */
  public createInvoicesForCalculation(calcController: CalculationCrudController, channel: PaymentChannel): Promise<Invoice[]> {
    if (!this.invoicesService.checkAccountVerificationForPayment()) {
      this.invoicesService.showMissingSignatureAlert();
      return;
    }

    if (!this.invoicesService.checkChangedSalaryDate(calcController.current)) {
      this.invoicesService.showChangedSalaryDateAlert();
      return;
    }

    if(InvoicesLogic.isEcfa(channel)) {
      if(!this.invoicesService.checkKycForPayment()) {
        this.invoicesService.showMissingKycAlert();
        return;
      }
    }

    const loader = this.uiHelpers.showLoading("SALAXY.NG1.InvoicesCreateComponent.PaymentChannel.creatingInvoices");
    return this.invoicesApi.createInvoices(calcController.current, channel).then((result) => {
      calcController.reload();
      loader.dismiss();
      this.showInvoiceSuccessAlert(channel);
      return result;
    });
  }

  /**
   * Create invoices for payroll: Immediate execution without preview. Used in PRO and other advanced UI.
   * Shows a loader dialog, saves changes if necessary, creates invoices and reloads the controller.
   * @param payrollController Controller that contains the calculation that should be paid. May have save changes.
   * @param channel Payment channel that is used for payment.
   */
  public createInvoicesForPayroll(payrollController: PayrollCrudController, channel: PaymentChannel): Promise<Invoice[]> {
    if (!this.invoicesService.checkAccountVerificationForPayment()) {
      this.invoicesService.showMissingSignatureAlert();
      return;
    }

    if (!this.invoicesService.checkChangedSalaryDate(payrollController.current)) {
      this.invoicesService.showChangedSalaryDateAlert();
      return;
    }

    if(InvoicesLogic.isEcfa(channel)) {
      if(!this.invoicesService.checkKycForPayment()) {
        this.invoicesService.showMissingKycAlert();
        return;
      }
    }

    const loader = this.uiHelpers.showLoading("SALAXY.NG1.InvoicesCreateComponent.PaymentChannel.creatingInvoices");

    const invoiceCreate = () => {
      return this.invoicesApi.createInvoicesForPayroll(channel, payrollController.current.id).then((result) => {
        payrollController.reload();
        loader.dismiss();
        this.showInvoiceSuccessAlert(channel);
        return result;
      });
    };

    if (payrollController.hasChanges || payrollController.isNew()) {
      return payrollController.save().then(() => {
        return invoiceCreate();
      });
    } else {
      return invoiceCreate();
    }
  }

  /** Gets text for preview invoice */
  public getPreviewText(item: InvoicePreview): string {
    if( (item.invoice.data.channel == PaymentChannel.PalkkausInvoices ||  item.invoice.data.channel == PaymentChannel.Kitsas) && (item.invoice.entityType == InvoiceType.Tax || item.invoice.entityType == InvoiceType.Union || item.invoice.entityType == InvoiceType.Payroll)) {
      return Translations.get("SALAXY.NG1.InvoicesCreateTableComponent.PalkkausInvoicesTaxPreviewText");
    }
    return Translations.get("SALAXY.NG1.InvoicesCreateTableComponent.dueDateTypePreview");
  }

  /** Gets text for estimation invoice */
  public getEstimationText(item: InvoicePreview): string {
    return `${item.invoice.otherPartyInfo.avatar.sortableName} ${Translations.get("SALAXY.NG1.InvoicesCreateTableComponent.sendsInvoice")}`;
  }

  /** Gets the type of the calculation for display purposes. */
  public getDueDateType(item: InvoicePreview): "default" | "estimation" | "forecast" | "preview" | "zero" | "unknown" | "paid" | "canceled" | "inProgress" {
    if (item.invoice.entityType === "unemployment") {
      return "estimation";
    }

    if (item.invoice.status === InvoiceStatus.Forecast) {
      return "forecast";
    }

    if (item.invoice.status === InvoiceStatus.Preview) {
      return "preview";
    }

    if (item.invoice.status === InvoiceStatus.Canceled) {
      return "canceled";
    }

    if (item.invoice.data.channel === PaymentChannel.ZeroPayment) {
      return "zero";
    }

    if (item.invoice.status === InvoiceStatus.Paid && item.invoice.payment === 0) {
      return "zero";
    }

    if (!item.invoice.otherId && item.invoice.data.channel === PaymentChannel.VismaNetvisor) {
      return "preview";
    }

    if (item.invoice.status === InvoiceStatus.Paid) {
      return "paid";
    }

    if (InvoicesLogic.isPalkkausManualLike(item.invoice.data.channel) && (item.invoice.entityType === InvoiceType.Net || item.invoice.entityType === InvoiceType.Foreclosure) && this.salaryDateKind === SalaryDateKind.InPast) {
      return "paid";
    }

    if (( InvoicesLogic.isPalkkausInvoicesLike(item.invoice.data.channel)) && (item.invoice.entityType === InvoiceType.Net || item.invoice.entityType === InvoiceType.Foreclosure) && this.salaryDateKind === SalaryDateKind.InPast) {
      return "paid";
    }

    if( InvoicesLogic.isPalkkausInvoicesLike(item.invoice.data.channel) &&
    (item.invoice.entityType == InvoiceType.Tax || item.invoice.entityType == InvoiceType.Union || item.invoice.entityType == InvoiceType.Payroll))
    {
      return "preview";
    }

    if ( ( item.invoice.entityType == "net" ||
           item.invoice.entityType == "foreclosure" ||
           item.invoice.entityType == "payroll") &&
      item.invoice.id != null && (
      item.invoice.status === InvoiceStatus.WaitingPalkkaus ||
      item.invoice.status === InvoiceStatus.Unread ||
      item.invoice.status === InvoiceStatus.WaitingConfirmation ||
      item.invoice.status === InvoiceStatus.Read ||
      item.invoice.status === InvoiceStatus.PaymentStarted ||
      item.invoice.status === InvoiceStatus.Warning )
    ) {
      return "inProgress";
    }

    return "default";
  }

  /**
   * Returns the net invoice preview for current calculation.
   */
  public get primaryCalcNetInvoicePreview(): InvoicePreview {
    if (!this._primaryCalcNetInvoicePreview && this.invoices && this.invoices.length > 0) {
      const preview = this.invoices.find((x) => x.invoice.entityType === InvoiceType.Net && x.invoice.status !== InvoiceStatus.Canceled && x.invoice.status !== InvoiceStatus.Error);
      if (preview) {
        this._primaryCalcNetInvoicePreview = preview;
      }
    }
    return this._primaryCalcNetInvoicePreview;
  }

  /**
   * Returns the ysc invoice preview for current calculation.
   */
  public get primaryCalcTaxInvoicePreview(): InvoicePreview {
    if (!this._primaryCalcTaxInvoicePreview && this.invoices && this.invoices.length > 0) {
      const preview = this.invoices.find((x) => x.invoice.entityType === InvoiceType.Tax && x.invoice.status !== InvoiceStatus.Canceled && x.invoice.status !== InvoiceStatus.Error);
      if (preview) {
        this._primaryCalcTaxInvoicePreview = preview;
      }
    }
    return this._primaryCalcTaxInvoicePreview;
  }

  private getNetInvoiceId(invoices: Invoice[]): string {
    if (!invoices || invoices.length === 0) {
      return null;
    }
    return (invoices.find((x) => x.header.type === InvoiceType.Net) || {}).id;
  }

  private showInvoiceSuccessAlert(channel: PaymentChannel) {
    if (channel === PaymentChannel.ZeroPayment) {
      this.uiHelpers.showAlert("SALAXY.NG1.InvoicesCreateComponent.PaymentChannel.zeroPayment.successAlertHeading", "SALAXY.NG1.InvoicesCreateComponent.PaymentChannel.zeroPayment.successAlertText");
    } else if (channel === PaymentChannel.AccountorGo) {
      this.uiHelpers.showAlert("SALAXY.NG1.InvoicesCreateComponent.PaymentChannel.accountorGo.successAlertHeading", "SALAXY.NG1.InvoicesCreateComponent.PaymentChannel.accountorGo.successAlertText");
    } else if (channel === PaymentChannel.FinagoSolo) {
      this.uiHelpers.showAlert("SALAXY.NG1.InvoicesCreateComponent.PaymentChannel.finagoSolo.successAlertHeading", "SALAXY.NG1.InvoicesCreateComponent.PaymentChannel.finagoSolo.successAlertText");
    } else if (channel === PaymentChannel.Kevytyrittaja) {
      this.uiHelpers.showAlert("SALAXY.NG1.InvoicesCreateComponent.PaymentChannel.kevytyrittaja.successAlertHeading", "SALAXY.NG1.InvoicesCreateComponent.PaymentChannel.kevytyrittaja.successAlertText");
    } else if (channel === PaymentChannel.Askelma) {
      this.uiHelpers.showAlert("SALAXY.NG1.InvoicesCreateComponent.PaymentChannel.askelma.successAlertHeading", "SALAXY.NG1.InvoicesCreateComponent.PaymentChannel.askelma.successAlertText");
    } else if (channel === PaymentChannel.Briox) {
      this.uiHelpers.showAlert("SALAXY.NG1.InvoicesCreateComponent.PaymentChannel.briox.successAlertHeading", "SALAXY.NG1.InvoicesCreateComponent.PaymentChannel.briox.successAlertText");
    } else if (channel === PaymentChannel.Lemonaid) {
      this.uiHelpers.showAlert("SALAXY.NG1.InvoicesCreateComponent.PaymentChannel.lemonaid.successAlertHeading", "SALAXY.NG1.InvoicesCreateComponent.PaymentChannel.lemonaid.successAlertText");
    } else if (channel === PaymentChannel.Laskupiste) {
      this.uiHelpers.showAlert("SALAXY.NG1.InvoicesCreateComponent.PaymentChannel.laskupiste.successAlertHeading", "SALAXY.NG1.InvoicesCreateComponent.PaymentChannel.laskupiste.successAlertText");
    } else if (channel === PaymentChannel.PasseliMerit) {
      this.uiHelpers.showAlert("SALAXY.NG1.InvoicesCreateComponent.PaymentChannel.passeliMerit.successAlertHeading", "SALAXY.NG1.InvoicesCreateComponent.PaymentChannel.passeliMerit.successAlertText");
    } else if (channel === PaymentChannel.Odoo) {
      this.uiHelpers.showAlert("SALAXY.NG1.InvoicesCreateComponent.PaymentChannel.odoo.successAlertHeading", "SALAXY.NG1.InvoicesCreateComponent.PaymentChannel.odoo.successAlertText");
    } else if (channel === PaymentChannel.Hausvise) {
      this.uiHelpers.showAlert("SALAXY.NG1.InvoicesCreateComponent.PaymentChannel.hausvise.successAlertHeading", "SALAXY.NG1.InvoicesCreateComponent.PaymentChannel.hausvise.successAlertText");
    }
    else if (channel === PaymentChannel.Ecom) {
      this.uiHelpers.showAlert("SALAXY.NG1.InvoicesCreateComponent.PaymentChannel.ecom.successAlertHeading", "SALAXY.NG1.InvoicesCreateComponent.PaymentChannel.ecom.successAlertText");
    } else if (channel === PaymentChannel.Kompassi) {
      this.uiHelpers.showAlert("SALAXY.NG1.InvoicesCreateComponent.PaymentChannel.kompassi.successAlertHeading", "SALAXY.NG1.InvoicesCreateComponent.PaymentChannel.kompassi.successAlertText");
    } else if (channel === PaymentChannel.Domus) {
      this.uiHelpers.showAlert("SALAXY.NG1.InvoicesCreateComponent.PaymentChannel.domus.successAlertHeading", "SALAXY.NG1.InvoicesCreateComponent.PaymentChannel.domus.successAlertText")
      .then(() => {
        window.parent.postMessage({ "salaxyEvent": "InvoiceSuccessAlert" }, "*");
        console.debug("Domus","InvoiceSuccessAlert posted with postMessage");
      });
    } else if (channel === PaymentChannel.Holvi) {
      this.uiHelpers.showAlert("SALAXY.NG1.InvoicesCreateComponent.PaymentChannel.holvi.successAlertHeading", "SALAXY.NG1.InvoicesCreateComponent.PaymentChannel.holvi.successAlertText");
    } else if (channel === PaymentChannel.TalenomOnline) {
      this.uiHelpers.showAlert("SALAXY.NG1.InvoicesCreateComponent.PaymentChannel.talenomOnline.successAlertHeading", "SALAXY.NG1.InvoicesCreateComponent.PaymentChannel.talenomOnline.successAlertText");
    } else if (InvoicesLogic.isPalkkausManualLike(channel)) {
      if (this.salaryDateKind == SalaryDateKind.InPast) {
        this.uiHelpers.showAlert("SALAXY.NG1.InvoicesCreateComponent.PaymentChannel.palkkausManualAndFixed.successAlertHeading", "SALAXY.NG1.InvoicesCreateComponent.PaymentChannel.palkkausManualAndFixed.successAlertText");
      }
      else {
        this.uiHelpers.showAlert("SALAXY.NG1.InvoicesCreateComponent.PaymentChannel.palkkausManual.successAlertHeading", "SALAXY.NG1.InvoicesCreateComponent.PaymentChannel.palkkausManual.successAlertText");
      }
    } else if (channel === PaymentChannel.PalkkausWS) {
      this.uiHelpers.showConfirm(
        "SALAXY.NG1.InvoicesCreateComponent.PaymentChannel.kitsas.successAlertHeading",
        null,
        "Maksa laskut heti",
        "Syötä lisää palkkoja",
        Translations.get("SALAXY.NG1.InvoicesCreateComponent.PaymentChannel.kitsas.successAlertText"),
      ).then((x) => {
        if (x) {
          this.$location.path("/invoices/payment-list");
        } else {
          // TODO: Now this only closes, should there be a redirect here?
        }
      });
    } else if (channel === PaymentChannel.PalkkausPersonal) {
      this.uiHelpers.showAlert("SALAXY.NG1.InvoicesCreateComponent.PaymentChannel.palkkausPersonal.successAlertHeading", "SALAXY.NG1.InvoicesCreateComponent.PaymentChannel.palkkausPersonal.successAlertText");
    } else if (channel === PaymentChannel.PalkkausInvoices) {
      this.uiHelpers.showConfirm(
        "SALAXY.NG1.InvoicesCreateComponent.PaymentChannel.palkkausInvoices.successAlertHeading",
        null,
        "SALAXY.NG1.InvoicesCreateComponent.PaymentChannel.palkkausInvoices.successAlertOk",
        "SALAXY.NG1.InvoicesCreateComponent.PaymentChannel.palkkausInvoices.successAlertCancel",
        Translations.get("SALAXY.NG1.InvoicesCreateComponent.PaymentChannel.palkkausInvoices.successAlertText"),
      ).then((x) => {
        if (x) {
          this.$location.path("/invoices/company");
        } else {
          // TODO: Now this only closes, should there be a redirect here?
        }
      });
    }
    else if (channel === PaymentChannel.Kitsas) {
      this.uiHelpers.showConfirm(
        "SALAXY.NG1.InvoicesCreateComponent.PaymentChannel.kitsas.successAlertHeading",
        null,
        "SALAXY.NG1.InvoicesCreateComponent.PaymentChannel.kitsas.successAlertOk",
        "SALAXY.NG1.InvoicesCreateComponent.PaymentChannel.kitsas.successAlertCancel",
        Translations.get("SALAXY.NG1.InvoicesCreateComponent.PaymentChannel.kitsas.successAlertText"),
      ).then((x) => {
        if (x) {
          this.$location.path("/invoices/company");
        } else {
          // TODO: Now this only closes, should there be a redirect here?
        }
      });
    }
  }
}
